import * as Sentry from "@sentry/react";
import { deleteObject, getStorage, ref } from "firebase/storage";
import { useState } from "react";
import styled from "styled-components";
import { v4 } from "uuid";
import { GenericSvg } from "../../../../assets/svgs/GenericSVG";
import { IconButton } from "../../../../core/components/buttons/IconButton";
import { MediaUpload } from "../../../../core/components/input/MediaUpload";
import { LoadingSpinner } from "../../../../core/components/loading/LoadingSpinner";
import { ModalContainer } from "../../../../core/components/modal/ModalContainer";
import { getCurrentPracticeId } from "../../../../core/store/chainSlice";
import { downscaleImageFile } from "../../../../core/utils/downscaleImageFile";
import {
  getPracticeFormId,
  getPracticePatientId,
  getUserAnswerToQuestion,
} from "../../../../core/store/currentFormSlice";
import { useMainStore } from "../../../../core/store/mainStore";
import { Body } from "../../../../core/styles/stringStyles";
import { useStyles } from "../../../../core/styles/theme";
import { uploadDocumentImage } from "../../../onboarding/utils/uploadDocumentImage";

type Props = {
  questionId: string;
  answerId: string;
};

const sanitizeFileName = (name: string) =>
  name.replace(/[^\w.-]+/g, "_").slice(-80);

export const DocumentUploadAnswer = (props: Props) => {
  const theme = useStyles();
  const [loading, setLoading] = useState(false);

  const practiceId = useMainStore(getCurrentPracticeId);
  const practicePatientId = useMainStore(getPracticePatientId);
  const practiceFormId = useMainStore(getPracticeFormId);
  const savedDocuments = useMainStore((state) =>
    getUserAnswerToQuestion(state, props.answerId),
  )?.documentUploadDetails;

  const setUserAnswerToQuestion = useMainStore(
    (state) => state.setUserAnswerToQuestion,
  );
  const deleteUserAnswerToQuestion = useMainStore(
    (state) => state.deleteUserAnswerToQuestion,
  );

  const handleNewFiles = async (files: FileList) => {
    // Shrink before upload: a recipe photo is several MB and patients are usually on mobile data.
    const newAssets = await Promise.all(
      Array.from(files).map(async (file) => ({
        id: v4(),
        file: await downscaleImageFile(file),
      })),
    );

    const uploadedAssets = await uploadAssets(newAssets);

    const updatedAssets = [
      ...(uploadedAssets ?? []),
      ...(savedDocuments ?? []),
    ];

    const patientAnswer = {
      id: props.answerId,
      documentUploadDetails: updatedAssets,
    };

    setUserAnswerToQuestion(props.questionId, patientAnswer, props.answerId);
    if (updatedAssets.length === 0) {
      deleteUserAnswerToQuestion(props.questionId, props.answerId);
    }
  };

  const uploadAssets = async (
    assets: {
      id: string;
      file?: File;
    }[],
  ): Promise<
    | {
        id: string;
        assetUrl: string;
        type: "image";
        internalName: string;
      }[]
    | undefined
  > => {
    setLoading(true);
    if (assets && assets.length > 0) {
      try {
        const uploadedAssets: {
          id: string;
          assetUrl: string;
          type: "image";
          internalName: string;
        }[] = [];
        for (const asset of assets) {
          if (!asset.file) {
            continue;
          }
          // The prefix is form-wide, so the per-asset uuid is the only thing keeping two camera
          // captures apart: iOS Safari names every one of them image.jpg (PF-5488).
          const bucketPath = `patientForms/${practiceId}/${practicePatientId}/documents/${practiceFormId}/${asset.id}-${sanitizeFileName(asset.file.name)}`;
          const uploadedAsset = await uploadDocumentImage(
            asset.file as File,
            bucketPath,
          );
          uploadedAssets.push({ id: asset.id, ...uploadedAsset });
        }
        const allAssets = [...(uploadedAssets || [])];
        setLoading(false);
        return allAssets;
      } catch (error) {
        // A failed batch is dropped silently by the caller, so it has to be visible somewhere.
        console.error("Error uploading recipe assets:", error);
        Sentry.captureException(error);
        setLoading(false);
      }
    } else {
      return undefined;
    }
  };

  const handleRemoveAsset = async (asset: {
    assetUrl: string;
    file?: File;
    type?: string;
    internalName: string;
  }) => {
    const storage = getStorage();
    const storageRef = ref(storage, asset.assetUrl);
    try {
      await deleteObject(storageRef);
    } catch (error) {
      console.error("Error removing asset from storage:", error);
    }

    const updatedAssets = savedDocuments?.filter(
      (a) => a.assetUrl !== asset.assetUrl,
    );

    const patientAnswer = {
      id: props.answerId,
      documentUploadDetails: updatedAssets,
    };
    setUserAnswerToQuestion(props.questionId, patientAnswer, props.answerId);

    if ((updatedAssets?.length ?? 0) === 0) {
      deleteUserAnswerToQuestion(props.questionId, props.answerId);
    }
  };

  return (
    <>
      <MediaUpload
        canUploadMultipleFiles
        handleUpload={handleNewFiles}
        type="file"
        placeHolderIcon="DocumentUpload"
      />
      {loading && (
        <LoadingContainer>
          <LoadingSpinner size={100} color={theme.colors.white} />
          <StyledBody>
            Dein Bild wird verarbeitet. Bitte habe einen Moment Geduld.
          </StyledBody>
        </LoadingContainer>
      )}

      <ImagesPreview>
        {savedDocuments?.map((asset) => (
          <ImagePreviewContainer
            key={asset?.assetUrl}
            $amountOfPreviews={Math.max(4, Math.min(savedDocuments.length, 6))}
          >
            {asset.mimeType?.includes("jpg") ||
            asset.mimeType?.includes("jpeg") ||
            asset.mimeType?.includes("png") ||
            asset.mimeType?.includes("gif") ? (
              <ImagePreview
                src={asset.assetUrl}
                onClick={() => handleRemoveAsset(asset)}
              />
            ) : (
              <GenericSvg
                name="Document"
                onClick={() => handleRemoveAsset(asset)}
                size={40}
              />
            )}
            <CloseButton
              iconName="Close"
              onClick={() => handleRemoveAsset(asset)}
              primaryColor={theme.colors.white}
              size={20}
            />
          </ImagePreviewContainer>
        ))}
      </ImagesPreview>
    </>
  );
};

const ImagePreview = styled.img`
  border-radius: 10px;
  overflow: hidden;
  object-fit: contain;
`;

const ImagesPreview = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 0;
  right: 0;
  display: none;
`;

const ImagePreviewContainer = styled.div<{
  $amountOfPreviews: number;
}>`
  height: 100px;
  width: calc((100% / ${({ $amountOfPreviews }) => $amountOfPreviews}) - 10px);
  max-width: calc(
    (100% / ${({ $amountOfPreviews }) => $amountOfPreviews}) - 10px
  );
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.light};

  &:hover {
    background-color: rgba(0, 0, 0, 0.3);
    ${CloseButton} {
      position: absolute;
      top: 0;
      right: 0;
      display: inherit;
    }
  }
`;

const LoadingContainer = styled(ModalContainer)`
  background-color: transparent !important;
  box-shadow: none !important;
`;

const StyledBody = styled(Body)`
  color: ${({ theme }) => theme.colors.white};
  margin-top: 20px;
  text-align: center;
`;
